import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Colors } from 'styles';
import type { ChipSize } from './Chip';

import { Chip, ChipOverlay } from './Chip';

export type ToggleChipProps = {
  size?: ChipSize;
  disabled?: boolean;
  onClick?: () => void;
  left?: boolean;
  right?: boolean;
  isSelected?: boolean;
  className?: string;
  children: React.ReactNode;
};

export const ToggleChip = forwardRef<HTMLButtonElement, ToggleChipProps>(
  function ForwardedToggleChip(
    {
      size = 'md',
      disabled = false,
      left = false,
      right = false,
      isSelected = false,
      onClick,
      className,
      children,
      ...rest
    }: ToggleChipProps,
    ref
  ) {
    if (!left && !right) return null;

    return (
      <BaseToggleChip
        {...rest}
        color="gray"
        variant="outline"
        size={size}
        disabled={disabled}
        left={left}
        right={right}
        isSelected={isSelected}
        className={className}
        onClick={onClick}
        ref={ref}
      >
        {children}
      </BaseToggleChip>
    );
  }
);

type BaseToggleChipProps = JSX.LibraryManagedAttributes<
  typeof Chip,
  React.ComponentProps<typeof Chip>
> & {
  left?: boolean;
  right?: boolean;
  isSelected?: boolean;
};

const BaseToggleChip = styled(Chip)`
  ${({ isSelected }: BaseToggleChipProps) =>
    isSelected === true &&
    css`
      background: ${Colors.gray5};
    `}
  ${({ left, right }: BaseToggleChipProps) => {
    if (left === true) {
      return css`
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;

        ${ChipOverlay} {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      `;
    }
    if (right === true) {
      return css`
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-left: none;

        ${ChipOverlay} {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      `;
    }
  }}
`;
